import NavigationService from '@disco/state-management/NavigationService'
import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { Thunk } from 'types/redux'
import api from '@disco/api/server'
import {
  CLIENT_LIBRARY_IMAGES_UPDATE,
  CLIENT_LIBRARY_IMAGES_UPDATE_FAIL,
  CLIENT_LIBRARY_IMAGES_UPDATE_SUCCESS,
  CLIENT_LIBRARY_LIST_CREATE,
  CLIENT_LIBRARY_LIST_CREATE_FAIL,
  CLIENT_LIBRARY_LIST_CREATE_SUCCESS,
  CLIENT_LIBRARY_LIST_DELETE,
  CLIENT_LIBRARY_LIST_DELETE_FAIL,
  CLIENT_LIBRARY_LIST_DELETE_SUCCESS,
  CLIENT_LIBRARY_LIST_GET,
  CLIENT_LIBRARY_LIST_GET_ALL,
  CLIENT_LIBRARY_LIST_GET_ALL_FAIL,
  CLIENT_LIBRARY_LIST_GET_ALL_SUCCESS,
  CLIENT_LIBRARY_LIST_GET_FAIL,
  CLIENT_LIBRARY_LIST_GET_SUCCESS,
  CLIENT_LIBRARY_UPDATE,
  CLIENT_LIBRARY_UPDATE_FAIL,
  CLIENT_LIBRARY_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { getPrimaryClientLibrary } from '@disco/state-management/selectors/client-libraries'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Loads all client libraries with optional count limit and error handling.
 * @param options - Configuration options for loading libraries
 * @param options.count - Optional limit on number of libraries to load
 * @param options.rethrowError - Whether to rethrow errors for upstream handling
 * @returns Thunk that loads all client libraries
 */
export function clientLibraryListGetAll(
  options: {
    count?: number
    rethrowError?: boolean
  } = {},
): Thunk {
  const { count, rethrowError } = options
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_LIST_GET_ALL,
    })

    try {
      const response = await api.getClientLibraries(count)
      return dispatch({
        type: CLIENT_LIBRARY_LIST_GET_ALL_SUCCESS,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_LIST_GET_ALL_FAIL,
        error,
      })
      if (rethrowError) throw error
    }
  }
}
/**
 * Loads a single client library by ID.
 * @param libraryId - ID of the client library to load
 * @returns Thunk that loads the specific client library
 */
export function clientLibraryListGetSingle(libraryId: ClientLibraryId): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_LIST_GET,
      libraryId,
    })

    try {
      const response = await api.getClientLibrary(libraryId)
      dispatch({
        type: CLIENT_LIBRARY_LIST_GET_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_LIST_GET_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Creates a new client library with the provided information.
 * @param libraryInfo - Library information including name
 * @returns Thunk that creates the library and redirects to library page
 */
export function clientLibraryListCreate(libraryInfo: { name: string }): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: CLIENT_LIBRARY_LIST_CREATE,
        libraryInfo,
      })
      const library = await api.createClientLibrary(libraryInfo)
      dispatch({
        type: CLIENT_LIBRARY_LIST_CREATE_SUCCESS,
        library,
      })
      NavigationService.push(`/catalogs/${library.id}`)
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: CLIENT_LIBRARY_LIST_CREATE_FAIL,
        error: error.body,
      })
    }
  }
}
/**
 * Deletes a client library by ID.
 * @param libraryId - ID of the client library to delete
 * @returns Thunk that deletes the client library
 */
export function clientLibraryListDelete(libraryId: ClientLibraryId): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: CLIENT_LIBRARY_LIST_DELETE,
        libraryId,
      })
      await api.deleteClientLibrary(libraryId)
      dispatch({
        type: CLIENT_LIBRARY_LIST_DELETE_SUCCESS,
        libraryId,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_LIST_DELETE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates client library information with optional notification control.
 * @param libraryId - ID of the client library to update
 * @param data - Partial library data to update
 * @param options - Optional configuration for update behavior
 * @param options.disableNotifications - Whether to suppress success notifications
 * @param options.rethrowError - Whether to rethrow errors for upstream handling (e.g. reverting an optimistic UI update)
 * @param options.successMessage - Optional message to display instead of the default success popup
 * @returns Thunk that updates library and handles primary library changes
 */
export function clientLibraryUpdate(
  libraryId: ClientLibraryId,
  data: Partial<ClientLibrary>,
  options?: {
    disableNotifications?: boolean
    rethrowError?: boolean
    successMessage?: string
  },
): Thunk {
  return async (dispatch, getState) => {
    const currentPrimaryLibraryId = getPrimaryClientLibrary(getState())?.id

    try {
      dispatch({
        type: CLIENT_LIBRARY_UPDATE,
        libraryId,
      })
      const response = await api.updateClientLibrary(libraryId, data)
      dispatch({
        type: CLIENT_LIBRARY_UPDATE_SUCCESS,
        libraryId,
        response,
      })

      if (Boolean(data.primary) && currentPrimaryLibraryId) {
        dispatch({
          type: CLIENT_LIBRARY_UPDATE_SUCCESS,
          libraryId: currentPrimaryLibraryId,
          response: {
            primary: false,
          },
        })
      }

      if (!options?.disableNotifications) {
        pushUINotification({
          message: options?.successMessage || 'Catalog updated!',
          level: 'success',
        })
      }
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: CLIENT_LIBRARY_UPDATE_FAIL,
        libraryId,
        error,
      })
      if (options?.rethrowError) {
        throw error
      }
    }
  }
}
/**
 * Updates client library images with optional upload progress tracking.
 * @param libraryId - ID of the client library to update images for
 * @param data - Partial library data containing image updates
 * @param onProgress - Optional callback to track upload progress percentage
 * @returns Thunk that updates library images
 */
export function clientLibraryImagesUpdate(
  libraryId: ClientLibraryId,
  data: Partial<ClientLibrary>,
  onProgress?: (percent: number) => unknown,
): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: CLIENT_LIBRARY_IMAGES_UPDATE,
        libraryId,
      })
      const response = await api.updateClientLibraryImages(
        libraryId,
        data,
        onProgress,
      )
      dispatch({
        type: CLIENT_LIBRARY_IMAGES_UPDATE_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_IMAGES_UPDATE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
